import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePromptLogin } from '@/context/LoginModalContext';
import { useCart } from '@/context/CartContext';

/* ─────────────────────────────────────────────────────────────
   Full-screen iframe modal
───────────────────────────────────────────────────────────────*/
const IframeModal = ({ url, onClose }) => (
    <div className="fixed inset-0 z-[9999] bg-black/80 flex flex-col" onClick={onClose}>
        <div className="flex justify-end p-3">
            <button onClick={onClose} className="text-white text-2xl leading-none bg-black/50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/70">×</button>
        </div>
        <div className="flex-1 mx-3 mb-3 rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <iframe src={url} className="w-full h-full border-0" title="Modal" allow="geolocation; camera; microphone" />
        </div>
    </div>
);

/* ─────────────────────────────────────────────────────────────
   Separator — separador.jpg image
───────────────────────────────────────────────────────────────*/
const Sep = () => (
    <img src="/icons/separador.jpg" alt="" aria-hidden="true"
        className="h-6 w-auto object-contain flex-shrink-0 opacity-40 select-none pointer-events-none" />
);

/* ─────────────────────────────────────────────────────────────
   App-bar logo item
   Desktop: fills a flex-1 slot, h-[30px]
   Mobile:  min-w-[140px] so only ~2 fit on screen at once
───────────────────────────────────────────────────────────────*/
const AppItem = ({ src, alt, href, isCurrent }) => {
    const img = (
        <img src={src} alt={alt}
            className={`h-[30px] w-full object-contain transition-all duration-150 ${
                isCurrent ? 'opacity-100' : 'grayscale opacity-60 hover:grayscale-0 hover:opacity-100'
            }`} />
    );
    const cls = 'flex items-center justify-center w-full';
    if (href === '/')  return <Link to={href} className={cls}>{img}</Link>;
    if (href === '#')  return <span className={cls}>{img}</span>;
    return <a href={href} className={cls}>{img}</a>;
};

/* ─────────────────────────────────────────────────────────────
   okYoSoy text fallback (no icon provided)
───────────────────────────────────────────────────────────────*/
const OkYoSoy = () => (
    <span className="flex-shrink-0 text-[11px] font-bold tracking-tight text-gray-400 leading-none select-none flex items-center justify-center">
        <span className="text-red-600">ok</span>YOSOY
    </span>
);

/* ─────────────────────────────────────────────────────────────
   Icon button for top-row action icons — 20 % bigger than before
   Previous: h-9 w-9 (36 px)  →  Now: h-11 w-11 (44 px)
───────────────────────────────────────────────────────────────*/
const IconBtn = ({ src, alt, onClick, title, style, className: cx = '' }) => (
    <button onClick={onClick} title={title || alt}
        className={`flex-shrink-0 flex items-center justify-center ${cx}`}
        style={style}>
        <img src={src} alt={alt} className="h-11 w-11 object-contain" />
    </button>
);

/* ─────────────────────────────────────────────────────────────
   APPS lists
───────────────────────────────────────────────────────────────*/
const APPS_OUT = [
    { id: 'okmarket',  src: '/icons/okmarket.png',       alt: 'okMarket',  href: 'https://app.okgo.mx/go/okmarket' },
    { id: 'okreserva', src: '/icons/okreserva.png',      alt: 'okReserva', href: 'https://app.okgo.mx/go/okreserva' },
    { id: 'okagenda',  src: '/icons/okagenda.png',       alt: 'okAgenda',  href: 'https://app.okgo.mx/go/okagenda' },
    { id: 'okviaja',   src: '/icons/okviaja.png',        alt: 'okViaja',   href: 'https://app.okgo.mx/go/okviaja' },
    { id: 'oklovendo', src: '/icons/oklovendo-logo.png', alt: 'okLoVendo', href: '/', isCurrent: true },
    { id: 'okfono',    src: '/icons/okfono.png',         alt: 'okFono',    href: 'https://app.okgo.mx/go/1738639726098x640093662191812600' },
];

const APPS_IN = [
    { id: 'okmarket',  src: '/icons/okmarket.png',       alt: 'okMarket',  href: 'https://app.okgo.mx/go/okmarket' },
    { id: 'okagenda',  src: '/icons/okagenda.png',       alt: 'okAgenda',  href: 'https://app.okgo.mx/go/okagenda' },
    { id: 'okviaja',   src: '/icons/okviaja.png',        alt: 'okViaja',   href: 'https://app.okgo.mx/go/okviaja' },
    { id: 'oklovendo', src: '/icons/oklovendo-logo.png', alt: 'okLoVendo', href: '/', isCurrent: true },
    { id: 'okfono',    src: '/icons/okfono.png',         alt: 'okFono',    href: 'https://app.okgo.mx/go/1738639726098x640093662191812600' },
    { id: 'okyosoy',   src: null,                        alt: 'okYoSoy',   href: '#' },
];

/* ─────────────────────────────────────────────────────────────
   App Bar Row
   ┌──────────────────────────────────────────────────────────┐
   │ DESKTOP: logos in equal flex-1 slots spanning 90% width  │
   │ MOBILE:  collapsed; only the ＋ button shows.            │
   │          Tapping ＋ expands the logo carousel.           │
   └──────────────────────────────────────────────────────────┘
───────────────────────────────────────────────────────────────*/
const AppBar = ({ apps, mobileOpen, onMobileToggle }) => (
    <div className="border-t bg-white">

        {/* ══ MOBILE ══ */}
        <div className="flex md:hidden items-center h-12">
            {/* Logo carousel – visible only when expanded */}
            {mobileOpen && (
                <div className="flex-1 overflow-x-auto scrollbar-hide min-w-0">
                    <div className="flex items-center h-12 pl-3 min-w-max" style={{ gap: '6px' }}>
                        {apps.map((app, i) => (
                            <div key={app.id} className="flex items-center" style={{ gap: '6px' }}>
                                {i > 0 && <Sep />}
                                {/* min-w ensures only ~2 logos fit before overflow */}
                                <div className="flex items-center justify-center" style={{ minWidth: '130px', height: '34px' }}>
                                    {app.src
                                        ? <AppItem src={app.src} alt={app.alt} href={app.href} isCurrent={!!app.isCurrent} />
                                        : <OkYoSoy />
                                    }
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ＋ toggle – always visible, anchored to the right */}
            <div className="flex-shrink-0 ml-auto pr-3 pl-2 self-stretch flex items-center">
                <button
                    onClick={onMobileToggle}
                    title={mobileOpen ? 'Cerrar apps' : 'Ver apps'}
                    className="w-10 h-10 rounded-xl bg-gray-900 hover:bg-black transition-colors flex items-center justify-center"
                >
                    <img src="/icons/icono-mas.png" alt="+" className="w-5 h-5 object-contain" />
                </button>
            </div>
        </div>

        {/* ══ DESKTOP ══ */}
        {/* Logos spread evenly across 90 % of the viewport with flex-1 slots */}
        <div className="hidden md:flex items-center h-12">
            <div className="flex items-center w-[90%] mx-auto h-full">
                {apps.map((app, i) => (
                    <div key={app.id} className="flex items-center flex-1 justify-center h-full">
                        {i > 0 && (
                            <div className="flex-shrink-0 px-1">
                                <Sep />
                            </div>
                        )}
                        <div className="flex-1 flex items-center justify-center px-1">
                            {app.src
                                ? <AppItem src={app.src} alt={app.alt} href={app.href} isCurrent={!!app.isCurrent} />
                                : <OkYoSoy />
                            }
                        </div>
                    </div>
                ))}
                {/* ＋ link at far right */}
                <div className="flex items-center flex-shrink-0 pl-2">
                    <Sep />
                    <a href="https://app.okgo.mx" title="Más apps"
                        className="ml-2 w-10 h-10 rounded-xl bg-gray-900 hover:bg-black transition-colors flex items-center justify-center">
                        <img src="/icons/icono-mas.png" alt="+" className="w-5 h-5 object-contain" />
                    </a>
                </div>
            </div>
        </div>

    </div>
);

/* ═════════════════════════════════════════════════════════════
   PUBLIC HEADER
═════════════════════════════════════════════════════════════*/
export const PublicHeader = () => {
    const { user, role } = useAuth();
    const promptLogin = usePromptLogin();
    const navigate = useNavigate();
    const { totalItems } = useCart();

    const [iframeUrl, setIframeUrl]         = useState(null);
    const [appBarVisible, setAppBarVisible] = useState(true);   // desktop toggle
    const [mobileLogoOpen, setMobileLogoOpen] = useState(false); // mobile carousel
    const lastScrollY = useRef(0);
    const isLoggedIn = role === 'user' && !!user;

    /* Collapse desktop app-bar on scroll-up when logged in */
    useEffect(() => {
        if (!isLoggedIn) return;
        const onScroll = () => {
            const y = window.scrollY;
            setAppBarVisible(y <= 20 || y < lastScrollY.current);
            lastScrollY.current = y;
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [isLoggedIn]);

    /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
       LOGGED-OUT
    ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/
    if (!isLoggedIn) {
        return (
            <>
                {iframeUrl && <IframeModal url={iframeUrl} onClose={() => setIframeUrl(null)} />}

                <header className="sticky top-0 z-40 bg-white shadow-sm">
                    {/*
                     * Top row — logo left, action icons horizontally centered
                     * Layout: [logo z-10] [absolute-center icons]
                     */}
                    <div className="relative flex items-center px-3 h-16">
                        {/* ok logo — left, above the centered layer */}
                        <Link to="/" className="flex-shrink-0 z-10">
                            <img src="/icons/ok-black-logo.png" alt="Ok" className="h-11 w-11 object-contain" />
                        </Link>

                        {/* Icons — absolute-centered in the row */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="flex items-center gap-2 pointer-events-auto">
                                <IconBtn src="/icons/ubicacion-icono.png" alt="Ubicación"
                                    onClick={() => navigator.geolocation?.getCurrentPosition(() => {})} />
                                <IconBtn src="/icons/okchat-icono.png" alt="OkChat"
                                    onClick={() => setIframeUrl('https://okchat.okgo.mx/superpoderes')} />
                                <IconBtn src="/icons/okbot-icono.png" alt="OkBot"
                                    onClick={() => setIframeUrl('https://okbot.okgo.mx/superpoderes')} />
                                {/* Ingreso button */}
                                <button onClick={() => promptLogin({ intent: 'generic' })}
                                    className="flex-shrink-0" title="Iniciar sesión">
                                    <img src="/icons/boton-ingreso.png" alt="Ingreso" className="h-11 object-contain" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* App bar — mobile: collapsed / desktop: always open */}
                    <AppBar
                        apps={APPS_OUT}
                        mobileOpen={mobileLogoOpen}
                        onMobileToggle={() => setMobileLogoOpen(v => !v)}
                    />
                </header>
            </>
        );
    }

    /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
       LOGGED-IN
    ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/
    return (
        <>
            {iframeUrl && <IframeModal url={iframeUrl} onClose={() => setIframeUrl(null)} />}

            <header className="sticky top-0 z-40 bg-white shadow-sm">
                {/*
                 * Top row
                 * Left:   ok-logo + go+ (no gap, same height)
                 * Center: action icons (absolute-centered, 20 % bigger)
                 */}
                <div className="relative flex items-center px-3 h-16">

                    {/* Left logos — z-10 so they sit above the centred icons layer */}
                    <div className="flex items-center flex-shrink-0 z-10">
                        <Link to="/">
                            <img src="/icons/ok-black-logo.png" alt="Ok" className="h-11 w-11 object-contain" />
                        </Link>
                        <img src="/icons/go-mas-logo.png" alt="go+" className="h-9 object-contain" />
                    </div>

                    {/* Action icons — absolute-centered in the full row */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="flex items-center gap-2 pointer-events-auto">

                            <IconBtn src="/icons/ubicacion-icono.png" alt="Ubicación"
                                onClick={() => navigator.geolocation?.getCurrentPosition(() => {})} />

                            <IconBtn src="/icons/okchat-icono.png" alt="OkChat"
                                onClick={() => setIframeUrl('https://okchat.okgo.mx/')} />

                            <IconBtn src="/icons/okbot-icono.png" alt="OkBot"
                                onClick={() => setIframeUrl('https://okbot.okgo.mx/')} />

                            {/* Carrito + badge */}
                            <button onClick={() => navigate('/carrito')} title="Carrito"
                                className="relative flex-shrink-0 flex items-center justify-center">
                                <img src="/icons/carrito-icono.png" alt="Carrito" className="h-11 w-11 object-contain" />
                                {totalItems > 0 && (
                                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
                                        {totalItems > 99 ? '99+' : totalItems}
                                    </span>
                                )}
                            </button>

                            {/*
                             * menu-icono-abierto.png → red, shown when app-bar IS visible
                             * menu-cerrado-icono.png → black, shown when app-bar IS hidden
                             * Pressing it toggles the desktop app-bar
                             */}
                            <button
                                onClick={() => setAppBarVisible(v => !v)}
                                title={appBarVisible ? 'Ocultar menú' : 'Mostrar menú'}
                                className="flex-shrink-0">
                                <img
                                    src={appBarVisible ? '/icons/menu-icono-abierto.png' : '/icons/menu-cerrado-icono.png'}
                                    alt="Toggle menú"
                                    className="h-11 w-11 object-contain"
                                />
                            </button>

                            {/* menu-lateral-icono.png — opens user menu / dashboard */}
                            <button
                                onClick={() => navigate('/dashboard')}
                                title="Menú de usuario"
                                className="flex-shrink-0">
                                <img src="/icons/menu-lateral-icono.png" alt="Menú usuario"
                                    className="h-11 w-11 object-contain" />
                            </button>

                            {/*
                             * icono-mas.png — secondary app-bar toggle
                             * Natural color (red) when bar is visible; black filter when hidden
                             */}
                            <button
                                onClick={() => setAppBarVisible(v => !v)}
                                title={appBarVisible ? 'Ocultar menú' : 'Mostrar menú'}
                                className="flex-shrink-0"
                                style={{ filter: appBarVisible ? 'none' : 'brightness(0)' }}>
                                <img src="/icons/icono-mas.png" alt="Más" className="h-11 w-11 object-contain" />
                            </button>

                        </div>
                    </div>
                </div>

                {/* Desktop app-bar — collapses via toggle or scroll */}
                <div className="hidden md:block overflow-hidden transition-all duration-300"
                    style={{ maxHeight: appBarVisible ? '56px' : '0px', opacity: appBarVisible ? 1 : 0 }}>
                    <AppBar
                        apps={APPS_IN}
                        mobileOpen={false}
                        onMobileToggle={() => {}}
                    />
                </div>

                {/* Mobile app-bar — always rendered, carousel toggle is inside AppBar */}
                <div className="md:hidden">
                    <AppBar
                        apps={APPS_IN}
                        mobileOpen={mobileLogoOpen}
                        onMobileToggle={() => setMobileLogoOpen(v => !v)}
                    />
                </div>
            </header>
        </>
    );
};
